/*
 * The Agents menu, opened from the sidebar's Agents button: plain rows of
 * agent runs, most urgent first. Each row has the agent's face, the client,
 * the visa and the time, then one status word in its tone and the one-line
 * result. Needs You lists the items waiting on you; All adds the runs still
 * working. Items stay until you answer, open, skip or dismiss them; a
 * dismissed thread comes back when it raises something new.
 */
import { useState, type FC, type ReactNode } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tag } from "@/components/ui/tag";
import { OPEN_THREAD_STATE, panelThreadPath } from "@/features/agents/routes";
import {
  useRunningTurnsStore,
  type RunningTurn
} from "@/features/agents/runningTurnsStore";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import { useAppTabsStore } from "@/features/appTabs/context/appTabsStore";
import type { AppTab } from "@/features/appTabs/types";
import { cn } from "@/lib/utils";

import {
  countsTowardBadge,
  itemRoute,
  signalKey,
  tabIdForScope,
  useAgentAttention
} from "../context/AgentAttentionProvider";
import { useAgentsTrayStore } from "../context/agentsTrayStore";
import { itemSeed, turnSeed } from "../faces";
import {
  byUrgency,
  signalAsk,
  signalClock,
  signalFace,
  type SignalFace
} from "../signalTone";
import type { AttentionItem } from "../types";
import { itemName } from "./SignalBody";

/** The banners' translucent surface; the menu itself is solid. */
export const GLASS =
  "rounded-3xl border border-slate-900/[0.07] bg-white/50 shadow-lg shadow-black/10 backdrop-blur-[20px] backdrop-saturate-[1.8] dark:bg-zinc-900/50";

/** A Working row's second line: the agent's current step, else the thread title, else when it started. */
export const workingLine = (
  turn: Pick<RunningTurn, "status" | "title" | "startedAt">,
  now?: Date
): string =>
  turn.status?.trim() ||
  turn.title?.trim() ||
  (turn.startedAt
    ? `Working since ${signalClock(turn.startedAt, now)}`
    : "Working");

/** Who a running turn is for, from the open tabs; practice turns read "Practice". */
export const workingClient = (
  turn: Pick<RunningTurn, "scope">,
  tabs: Pick<AppTab, "id" | "title" | "workflow">[]
): { name: string; tag: string | null } => {
  if (turn.scope.kind === "practice") return { name: "Practice", tag: null };
  const tab = tabs.find(
    (t) => t.id === tabIdForScope(turn.scope.kind, turn.scope.id)
  );
  return {
    name: tab?.title || (turn.scope.kind === "case" ? "Case" : "Client"),
    tag: tab?.workflow ?? null
  };
};

/** The status word on a row's second line, and its tone. */
const STATUS: Record<SignalFace, { word: string; tone: string }> = {
  approval: { word: "Approval", tone: "text-warn" },
  question: { word: "Question", tone: "text-warn" },
  failed: { word: "Failed", tone: "text-danger" },
  problems: { word: "Problems", tone: "text-danger" },
  finished: { word: "Finished", tone: "text-success" }
};

type View = "needs" | "all";

const rowClass =
  "group/row flex w-full cursor-pointer items-start gap-2.5 rounded-md py-2 pr-2 pl-2.5 text-left transition-colors duration-100 outline-none hover:bg-fill-1 focus-visible:bg-fill-1";

const RowBody: FC<{
  name: string;
  tag: string | null;
  time: string;
  status: string;
  tone: string;
  line: string;
  /** Under the time, so every row's time keeps the right edge. */
  action?: ReactNode;
  /** Takes the time's place while the row is hovered or focused. */
  dismiss?: ReactNode;
}> = ({ name, tag, time, status, tone, line, action, dismiss }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="truncate text-base font-medium text-fg">{name}</span>
      {tag ? <Tag>{tag}</Tag> : null}
      <span className="flex-1" />
      {time ? (
        <span
          className={cn(
            "shrink-0 text-sm text-fg-4",
            dismiss && "group-focus-within/row:hidden group-hover/row:hidden"
          )}
        >
          {time}
        </span>
      ) : null}
      {dismiss ? (
        <span
          className={cn(
            "-my-0.5 shrink-0 group-focus-within/row:inline-flex group-hover/row:inline-flex",
            time ? "hidden" : "inline-flex"
          )}
        >
          {dismiss}
        </span>
      ) : null}
    </span>
    <span className="flex min-w-0 items-center gap-1.5 text-sm">
      <span className={cn("shrink-0 font-medium", tone)}>{status}</span>
      <span className="min-w-0 flex-1 truncate text-fg-2">{line}</span>
      {action}
    </span>
  </div>
);

const AttentionRow: FC<{
  item: AttentionItem;
  onOpen: (item: AttentionItem) => void;
  onDismiss: (item: AttentionItem) => void;
  onSkip: (item: AttentionItem) => void;
}> = ({ item, onOpen, onDismiss, onSkip }) => {
  const face = signalFace(item);
  const status = STATUS[face];
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${itemName(item)}: ${status.word}, ${signalAsk(item)}`}
      data-testid="agents-menu-item"
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item);
        }
      }}
      className={rowClass}
    >
      <AgentMoodAvatar
        agent={{ key: itemSeed(item) }}
        className="size-6 shrink-0 rounded-full"
      />
      <RowBody
        name={itemName(item)}
        tag={item.visa_type}
        time={signalClock(item.since)}
        status={status.word}
        tone={status.tone}
        line={signalAsk(item)}
        dismiss={
          <Button
            variant="ghost"
            size="icon-2xs"
            aria-label="Dismiss"
            title="Dismiss"
            data-testid="agents-menu-dismiss"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(item);
            }}
          >
            <X />
          </Button>
        }
        action={
          face === "question" ? (
            <span className="-my-1 flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="xs"
                title="Skip Without Answering"
                data-testid="agents-menu-skip"
                onClick={(e) => {
                  e.stopPropagation();
                  onSkip(item);
                }}
              >
                Skip
              </Button>
              <Button
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(item);
                }}
              >
                Answer
              </Button>
            </span>
          ) : null
        }
      />
    </div>
  );
};

const WorkingRow: FC<{
  turn: RunningTurn;
  tabs: AppTab[];
  onOpen: (turn: RunningTurn) => void;
}> = ({ turn, tabs, onOpen }) => {
  const who = workingClient(turn, tabs);
  return (
    <button
      type="button"
      data-testid="agents-menu-working"
      aria-label={`${who.name}: ${workingLine(turn)}`}
      onClick={() => onOpen(turn)}
      className={rowClass}
    >
      <AgentMoodAvatar
        animated
        agent={{ key: turnSeed(turn) }}
        className="size-6 shrink-0 rounded-full"
      />
      <RowBody
        name={who.name}
        tag={who.tag}
        time={turn.startedAt ? signalClock(turn.startedAt) : ""}
        status="Working"
        tone="text-fg-3"
        line={workingLine(turn)}
      />
    </button>
  );
};

export const AgentsMenu: FC<{ className?: string }> = ({ className }) => {
  const navigate = useNavigate();
  const { items, count, dismiss, skip } = useAgentAttention();
  const running = useRunningTurnsStore((s) => s.turns);
  const tabs = useAppTabsStore((s) => s.tabs);
  const setOpen = useAgentsTrayStore((s) => s.setOpen);
  const [view, setView] = useState<View>("needs");
  const [showEarlier, setShowEarlier] = useState(false);
  const sorted = byUrgency(items);
  // The header counts what the badge counts; snoozed items and old finishes
  // wait under "Earlier".
  const current = sorted.filter((i) => countsTowardBadge(i));
  const earlier = sorted.filter((i) => !countsTowardBadge(i));

  const go = (path: string) => {
    setOpen(false);
    navigate(path, { state: OPEN_THREAD_STATE });
  };

  const onDismiss = async (targets: AttentionItem[]) => {
    const ok = await dismiss(targets);
    if (!ok) toast.error("Couldn't dismiss. Try again.");
    else if (targets.length > 1)
      toast(`Dismissed ${targets.length}. New activity brings them back.`);
  };

  const onSkip = async (item: AttentionItem) => {
    try {
      await skip(item);
      toast("Skipped. The agent carries on without an answer.");
    } catch {
      toast.error("Couldn't skip that question. Open it to answer.");
    }
  };

  const row = (item: AttentionItem) => (
    <AttentionRow
      key={signalKey(item)}
      item={item}
      onOpen={(i) => go(itemRoute(i))}
      onDismiss={(i) => void onDismiss([i])}
      onSkip={(i) => void onSkip(i)}
    />
  );

  const summary = count
    ? `${count} need${count === 1 ? "s" : ""} attention`
    : running.length
    ? `${running.length} working`
    : "All quiet";
  const showWorking = view === "all" && running.length > 0;

  return (
    <div
      role="dialog"
      aria-label="Agents"
      data-testid="agents-menu"
      className={cn(
        "signal-fade flex max-h-[min(560px,70vh)] w-110 flex-col overflow-hidden rounded-xl bg-elevated shadow-popover ring-1 ring-line-3",
        className
      )}
    >
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-line-2 pr-2 pl-3.5">
        <span className="text-base font-medium text-fg">Agents</span>
        <span className="min-w-0 flex-1 truncate text-sm text-fg-3">
          {summary}
        </span>
        <Tabs
          value={view}
          onValueChange={(v) => setView(v === "all" ? "all" : "needs")}
        >
          <TabsList aria-label="Which runs">
            <TabsTrigger value="needs" className="text-sm">
              Needs You
            </TabsTrigger>
            <TabsTrigger value="all" className="text-sm">
              All
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {current.map(row)}
        {showWorking
          ? running.map((turn) => (
              <WorkingRow
                key={turn.threadId}
                turn={turn}
                tabs={tabs}
                onOpen={(t) => go(panelThreadPath(t.scope, t.threadId))}
              />
            ))
          : null}
        {!current.length && !showWorking ? (
          <p className="px-2.5 py-2 text-sm text-fg-3">You're caught up.</p>
        ) : null}
        {earlier.length ? (
          <Button
            variant="ghost"
            size="xs"
            data-testid="agents-menu-earlier"
            aria-expanded={showEarlier}
            className="mx-1.5 my-1"
            onClick={() => setShowEarlier((v) => !v)}
          >
            {showEarlier ? "Hide Earlier" : `${earlier.length} Earlier`}
          </Button>
        ) : null}
        {showEarlier ? earlier.map(row) : null}
      </div>
      <div className="flex h-9 shrink-0 items-center gap-2 border-t border-line-2 px-3.5 text-sm text-fg-3">
        <span
          aria-hidden
          className={cn(
            "size-1.5 shrink-0 rounded-full",
            running.length ? "bg-success" : "bg-fg-4"
          )}
        />
        Working ·{" "}
        {running.length ? `${running.length} Running` : "Nothing Running"}
        {sorted.length ? (
          <Button
            variant="ghost"
            size="xs"
            data-testid="agents-menu-dismiss-all"
            className="-mr-2 ml-auto"
            onClick={() => void onDismiss(sorted)}
          >
            Dismiss All
          </Button>
        ) : null}
      </div>
    </div>
  );
};
